/**
 * DelegateRodeosPage — Admin/Delegate rodeo list
 *
 * Shows rodeos the current user manages (via their organizations),
 * with tabs for drafts vs published, and links to create/edit.
 *
 * Uses `useMyRodeos()` which scopes results server-side to the caller's
 * org memberships, so no client-side org filter is needed.
 *
 * Expand state is a `Set<string>` so multiple rodeos can be open at once.
 */

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { RodeoListItem } from '@/components/delegate/RodeoListItem';
import { useMyRodeos } from '@/hooks/useMyRodeos';
import { useDeleteRodeo } from '@/hooks/useDeleteRodeo';
import { useToast } from '@/contexts/ToastContext';
import { isPastSchedule, sortScheduleByTab } from '@/utils/scheduleTabs';

type TabValue = 'drafts' | 'published' | 'all' | 'upcoming' | 'past';

const TAB_VALUES: TabValue[] = ['all', 'drafts', 'published', 'upcoming', 'past'];

// ─── Persisted UI state ─────────────────────────────────────────────
// Remember the last-selected tab AND the set of expanded rodeo cards *per
// tab* so both survive a page refresh and tab switches. This mirrors the
// delegate Live Scoring landing list (EXPANDED_CARDS_STORAGE_KEY /
// SCHEDULE_TAB_STORAGE_KEY in DelegateLiveScoringPage) — same session-scoped
// storage model: a reload within the tab keeps the state, closing the tab
// clears it. Keys are namespaced to this page so they never collide with the
// Draw / Live Scoring state. The one extension over Live Scoring is that
// expansion is keyed by tab, since each tab shows a different rodeo subset.
const TAB_STORAGE_KEY = 'highcall:delegate-my-rodeos:tab';
const EXPANDED_CARDS_STORAGE_KEY = 'highcall:delegate-my-rodeos:expanded-cards';

type ExpandedByTab = Record<TabValue, Set<string>>;

function emptyExpandedByTab(): ExpandedByTab {
  return { all: new Set(), drafts: new Set(), published: new Set(), upcoming: new Set(), past: new Set() };
}

function isTabValue(value: unknown): value is TabValue {
  return typeof value === 'string' && (TAB_VALUES as string[]).includes(value);
}

function loadTab(): TabValue {
  if (typeof window === 'undefined') return 'all';
  try {
    const raw = window.sessionStorage.getItem(TAB_STORAGE_KEY);
    return isTabValue(raw) ? raw : 'all';
  } catch {
    return 'all';
  }
}

function saveTab(tab: TabValue): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(TAB_STORAGE_KEY, tab);
  } catch {
    // sessionStorage may be unavailable (e.g. quota / disabled); ignore.
  }
}

function loadExpandedByTab(): ExpandedByTab {
  const result = emptyExpandedByTab();
  if (typeof window === 'undefined') return result;
  try {
    const raw = window.sessionStorage.getItem(EXPANDED_CARDS_STORAGE_KEY);
    if (!raw) return result;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      for (const t of TAB_VALUES) {
        const ids = (parsed as Record<string, unknown>)[t];
        if (Array.isArray(ids)) {
          result[t] = new Set(ids.filter((x): x is string => typeof x === 'string'));
        }
      }
    }
  } catch {
    // Malformed or unavailable storage — fall back to all-collapsed.
  }
  return result;
}

function saveExpandedByTab(state: ExpandedByTab): void {
  if (typeof window === 'undefined') return;
  try {
    const serializable: Record<string, string[]> = {};
    for (const t of TAB_VALUES) serializable[t] = Array.from(state[t]);
    window.sessionStorage.setItem(EXPANDED_CARDS_STORAGE_KEY, JSON.stringify(serializable));
  } catch {
    // sessionStorage may be unavailable (e.g. quota / disabled); ignore.
  }
}

export default function DelegateRodeosPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabValue>(() => loadTab());
  const handleTabChange = (next: TabValue) => {
    setTab(next);
    saveTab(next);
  };

  // Controlled accordion expansion state — multiple rodeos can be open at once,
  // tracked per tab and persisted so refresh / tab switches keep the open cards.
  const [expandedByTab, setExpandedByTab] = useState<ExpandedByTab>(() => loadExpandedByTab());
  const toggleExpanded = (tabKey: TabValue, id: string) => {
    setExpandedByTab((prev) => {
      const nextSet = new Set(prev[tabKey]);
      if (nextSet.has(id)) nextSet.delete(id);
      else nextSet.add(id);
      const updated = { ...prev, [tabKey]: nextSet };
      saveExpandedByTab(updated);
      return updated;
    });
  };

  // Delete confirmation state — page-level, mirrors DelegateJackpotsPage
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const deleteRodeo = useDeleteRodeo();
  const { showError } = useToast();

  const handleDeleteConfirm = async () => {
    if (!deleteId) return;
    try {
      await deleteRodeo.mutateAsync(deleteId);
      setDeleteId(null);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'An unexpected error occurred.';
      showError('Delete failed', msg);
    }
  };

  // Fetch rodeos scoped to the caller's orgs (server-side)
  const { data, isLoading } = useMyRodeos({ page_size: 100 });
  const rawRodeos = data?.data ?? [];

  // Sort newest-first by start_date. Rodeos without a start_date are
  // pushed to the end so the "real" rodeos dominate the top of the list.
  // `localeCompare` on ISO `YYYY-MM-DD` strings is equivalent to a date
  // comparison, so we don't need to parse into Date objects.
  //
  // Upcoming / Past use the shared finished-check + sort comparator from
  // `@/utils/scheduleTabs` (the same helpers the delegate Draws and Live
  // Scoring lists use), so upcoming/past classification stays timezone-aware
  // and consistent across screens: Upcoming = today-or-future, soonest first;
  // Past = already finished, most recently finished first.
  const { allRodeos, drafts, published, upcoming, past } = useMemo(() => {
    const sorted = [...rawRodeos].sort((a: any, b: any) => {
      const aKey = a?.start_date ?? '';
      const bKey = b?.start_date ?? '';
      if (!aKey && !bKey) return 0;
      if (!aKey) return 1;
      if (!bKey) return -1;
      return bKey.localeCompare(aKey);
    });
    const d: typeof sorted = [];
    const p: typeof sorted = [];
    const up: typeof sorted = [];
    const pa: typeof sorted = [];
    for (const r of sorted) {
      if (r.is_published) {
        p.push(r);
      } else {
        d.push(r);
      }
      if (isPastSchedule(r)) {
        pa.push(r);
      } else {
        up.push(r);
      }
    }
    return {
      allRodeos: sorted,
      drafts: d,
      published: p,
      upcoming: sortScheduleByTab(up, 'upcoming'),
      past: sortScheduleByTab(pa, 'past'),
    };
  }, [rawRodeos]);

  const displayed =
    tab === 'drafts'
      ? drafts
      : tab === 'published'
        ? published
        : tab === 'upcoming'
          ? upcoming
          : tab === 'past'
            ? past
            : allRodeos;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold">My Rodeos</h1>
          <p className="text-sm text-muted-foreground">
            Manage your rodeo events and configurations.
          </p>
        </div>
        <Button onClick={() => navigate('/delegate/rodeos/create')}>
          <Plus className="h-4 w-4 mr-2" /> Create Rodeo
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={(v) => handleTabChange(v as TabValue)}>
        <TabsList>
          <TabsTrigger value="all" className="gap-1.5">
            All
            <Badge variant="secondary" className="text-xs px-1.5 py-0">
              {allRodeos.length}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="drafts" className="gap-1.5">
            Drafts
            <Badge variant="secondary" className="text-xs px-1.5 py-0">
              {drafts.length}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="published" className="gap-1.5">
            Published
            <Badge variant="secondary" className="text-xs px-1.5 py-0">
              {published.length}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="upcoming" className="gap-1.5">
            Upcoming
            <Badge variant="secondary" className="text-xs px-1.5 py-0">
              {upcoming.length}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="past" className="gap-1.5">
            Past
            <Badge variant="secondary" className="text-xs px-1.5 py-0">
              {past.length}
            </Badge>
          </TabsTrigger>
        </TabsList>

        {TAB_VALUES.map((t) => (
          <TabsContent key={t} value={t} className="mt-4">
            {isLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-14 rounded-lg" />
                ))}
              </div>
            ) : displayed.length === 0 ? (
              <EmptyState
                icon={t === 'drafts' ? Search : Calendar}
                title={
                  t === 'drafts'
                    ? 'No draft rodeos'
                    : t === 'published'
                      ? 'No published rodeos'
                      : t === 'upcoming'
                        ? 'No upcoming rodeos'
                        : t === 'past'
                          ? 'No past rodeos'
                          : 'No rodeos yet'
                }
                description={
                  t === 'drafts'
                    ? 'Your unpublished rodeos will appear here.'
                    : t === 'published'
                      ? 'Published rodeos will appear here.'
                      : t === 'upcoming'
                        ? 'Rodeos scheduled for today or later will appear here.'
                        : t === 'past'
                          ? 'Rodeos that have already finished will appear here.'
                          : 'Create your first rodeo to get started.'
                }
                action={
                  t !== 'published' && t !== 'past'
                    ? {
                        label: 'Create Rodeo',
                        onClick: () => navigate('/delegate/rodeos/create'),
                      }
                    : undefined
                }
              />
            ) : (
              <div className="space-y-2">
                {displayed.map((r: any) => (
                  <RodeoListItem
                    key={r.id}
                    rodeo={r}
                    isExpanded={expandedByTab[t].has(r.id)}
                    onToggle={() => toggleExpanded(t, r.id)}
                    onDelete={setDeleteId}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* Delete confirmation dialog */}
      <Dialog
        open={!!deleteId}
        onOpenChange={(open) => {
          if (!open) setDeleteId(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Rodeo</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this rodeo? All performances and
              events within the rodeo will also be deleted. This action cannot
              be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deleteRodeo.isPending}
            >
              {deleteRodeo.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
